/**
 * Tiering rules: copy, move or delete files between storage locations some
 * time after ingest (or arrival, or last access), or (0.98.0) move or delete
 * the least recently used files when the source fills up (watermarks). Shown
 * under Admin → Storage, run in order every few minutes, with a dry run, Run
 * now and an On / Paused switch per rule.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Play, Plus, Search, Trash2 } from 'lucide-react';
import type { StorageLocationSummary, StorageRuleDTO, StorageRuleResult, StorageRuleSaved } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useCategories } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { TableScroll } from '../../components/TableScroll';
import { toast } from '../../components/Toasts';
import { bytes, dateTime, relative } from '../../lib/format';
import { queryKeys } from '../../api/queryKeys';
import { diskBelowQuotaNote, quotaUsedLabel } from '../../lib/quota';
import { useTransferWindows } from '../../api/hooks/transferWindows';
import { TransferWindowPicker } from './storage/TransferWindows';

const FILES = { original: 'Originals', proxy: 'Proxies', rendition: 'Renditions', all: 'Originals, proxies and renditions' } as const;
const ACTION = { copy: 'Copy', move: 'Move', delete: 'Delete' } as const;

const days = (n: number) => (n === 0 ? 'straight away' : n === 1 ? 'after 1 day' : `after ${n} days`);

const AGE_FROM = { ingest: 'ingest', arrival: 'arriving there', access: 'last use' } as const;
const pct = (n: number | null | undefined) => `${Math.round(n ?? 0)} %`;
const files = (n: number) => `${n} file${n === 1 ? '' : 's'}`;

/** Rule runs outside their transfer window say this (services/api storage/rules.ts OUTSIDE_WINDOW). */
const OUTSIDE_WINDOW = 'Outside its window';

type Describable = Pick<StorageRuleDTO, 'files' | 'mediaKind' | 'category' | 'trigger' | 'afterDays' | 'ageFrom' | 'action' | 'source' | 'destination' | 'keepCopy' | 'highWatermarkPct' | 'lowWatermarkPct'>
  & Partial<Pick<StorageRuleDTO, 'window' | 'maxConcurrent' | 'primaryOnly' | 'versions'>>;

export function describeRule(r: Describable): string {
  // 1.1.0: when it may run, and how many transfers at once.
  const limits = `${r.window ? `, only during ${r.window.name}` : ''}${r.maxConcurrent && r.action !== 'delete' ? `, at most ${r.maxConcurrent} at once` : ''}`;
  return `${describeWhat(r)}${limits}`;
}

function describeWhat(r: Describable): string {
  // 1.2.0: originals include every master of a version, unless the rule is for primary masters only.
  const kind = r.primaryOnly && (r.files === 'original' || r.files === 'all') ? `${FILES[r.files].toLowerCase()} (primary masters only)` : FILES[r.files].toLowerCase();
  // 1.2.3: only the files of versions that aren't their asset's current one.
  const versions = r.versions === 'noncurrent' ? ' of earlier versions' : '';
  const what = `${kind}${versions}${r.mediaKind ? ` of ${r.mediaKind} assets` : ''}${r.category ? ` in class ${r.category.name}` : ''}`;
  if (r.trigger === 'watermark') {
    const until = `until it's ${pct(r.lowWatermarkPct)}`;
    const start = `When ${r.source.name} is ${pct(r.highWatermarkPct)} full`;
    if (r.action === 'delete') return `${start}, delete least recently used ${what} that have a verified copy in ${r.keepCopy?.name ?? 'another location'} ${until}`;
    return `${start}, move least recently used ${what} to ${r.destination?.name} ${until}`;
  }
  const after = r.afterDays ?? 0;
  const when = after === 0 ? 'straight away' : `${days(after)} from ${AGE_FROM[r.ageFrom]}`;
  if (r.action === 'delete') return `Delete ${what} from ${r.source.name} ${when}, once a verified copy is in ${r.keepCopy?.name ?? 'another location'}`;
  return `${ACTION[r.action]} ${what} from ${r.source.name} to ${r.destination?.name} ${when}`;
}

const resultText = (r: StorageRuleResult) => {
  const w = r.watermark;
  if (w) {
    if (w.basis === 'none') return 'No quota or disk size';
    if (w.stoppedBy === 'window_closed') return `Waiting for its window: ${pct(w.usedPct)} full${w.draining ? ', draining' : ''}`;
    if (r.dryRun) return w.draining ? `${pct(w.usedPct)} full: ${files(r.matched)} (${bytes(w.plannedBytes)}) would go now` : `${pct(w.usedPct)} full, below ${pct(w.highPct)}`;
    if (!w.draining) return `${pct(w.usedPct)} full, below ${pct(w.highPct)}`;
    const n = r.moved + r.deleted;
    if (w.stoppedBy === 'destination_full' && n === 0) return 'Destination full';
    return `Draining: ${pct(w.usedPct)} → ${pct(w.lowPct)}, ${files(n)} (${bytes(w.plannedBytes)}) ${r.deleted ? 'deleted' : 'queued'}${w.stoppedBy === 'destination_full' ? ' · destination full' : ''}`;
  }
  if (!r.dryRun && r.errors[0] === OUTSIDE_WINDOW && r.matched === 0) return 'Waiting for its window';
  const done = [r.copied && `${r.copied} copied`, r.moved && `${r.moved} moved`, r.deleted && `${r.deleted} deleted`, r.skipped && `${r.skipped} skipped`].filter(Boolean);
  return r.dryRun ? `${r.matched} file${r.matched === 1 ? '' : 's'} would be affected now` : done.length ? done.join(', ') : 'Nothing to do';
};

/** The last run's outcome, when, and its first note (the window note is in the outcome already). */
function LastRun({ r, result }: { r: StorageRuleDTO; result: StorageRuleResult }) {
  const notes = result.errors.filter((e) => e !== OUTSIDE_WINDOW);
  return (
    <>
      <span data-testid="rule-last-result">{resultText(result)}</span>
      <div className="faint" style={{ fontSize: 12 }} title={notes.join('\n')}>{relative(r.lastRunAt)}{notes.length ? ` · ${notes[0]}` : ''}</div>
    </>
  );
}

/** How full a source is now, for the watermark fields: "Local media is 62 % full (of its 30 GB quota)". */
function fillText(l: StorageLocationSummary): string | null {
  const f = l.fill;
  if (!f || f.basis === 'none' || f.pct == null) return null;
  return `${l.name} is ${pct(f.pct)} full (of its ${f.basis === 'quota' ? `${bytes(f.totalBytes)} quota` : `disk, ${bytes(f.totalBytes)}`})`;
}

export function TieringRules({ locations }: { locations: StorageLocationSummary[] }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['storage-rules'], queryFn: () => api<{ items: StorageRuleDTO[] }>('/api/v1/storage-rules') });
  const [editing, setEditing] = useState<StorageRuleDTO | 'new' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const rules = q.data?.items ?? [];
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['storage-rules'] }), qc.invalidateQueries({ queryKey: queryKeys.storage() })]);

  async function run(r: StorageRuleDTO, dryRun: boolean) {
    setBusy(r.id);
    try {
      const res = await api<StorageRuleResult>(`/api/v1/storage-rules/${r.id}/run${dryRun ? '?dryRun=true' : ''}`, { method: 'POST', json: {} });
      toast(`${r.name}: ${resultText(res)}${res.errors.length ? ` (${res.errors[0]})` : ''}`, res.skipped ? 'error' : undefined);
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }
  async function move(i: number, by: number) {
    const ids = rules.map((r) => r.id);
    const [x] = ids.splice(i, 1);
    ids.splice(i + by, 0, x!);
    await api('/api/v1/storage-rules-order', { method: 'PUT', json: { ids } });
    await refresh();
  }
  async function setEnabled(r: StorageRuleDTO, enabled: boolean) {
    setBusy(r.id);
    try {
      await api(`/api/v1/storage-rules/${r.id}`, { method: 'PATCH', json: { enabled } });
      await refresh();
      toast(enabled ? `${r.name} is on` : `Paused ${r.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }
  async function remove(r: StorageRuleDTO) {
    if (!window.confirm(`Delete the rule “${r.name}”? Files it already copied, moved or deleted stay as they are.`)) return;
    await api(`/api/v1/storage-rules/${r.id}`, { method: 'DELETE' });
    await refresh();
    toast(`Deleted ${r.name}`);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 32 }}>
        <h2 style={{ margin: 0 }}>Tiering rules</h2>
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={() => setEditing('new')} data-testid="add-rule"><Plus size={14} /> Add rule</button>
      </div>
      <p className="lede">
        Copy, move or delete files between locations some time after ingest, or move or delete the least recently used files when a location fills up.
        Rules run in this order every few minutes. Copies and moves are verified by checksum and respect quotas; a delete only happens when a verified copy
        exists elsewhere, so the last copy is never removed. Combine rules, for example copy new originals to a second location straight away, then delete
        them from the first after 30 days.
      </p>
      <div className="panel-card" style={{ padding: 0 }} data-testid="storage-rules">
        {rules.length === 0 ? <p className="muted" style={{ padding: 16, margin: 0 }}>No rules yet: files stay where they are stored.</p> : (
          <TableScroll>
          <table className="table compact">
            <thead><tr><th style={{ width: 70 }}>Order</th><th>Rule</th><th style={{ width: 96 }}>Runs</th><th>Last run</th><th /></tr></thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={r.id} data-testid="storage-rule" data-name={r.name} data-enabled={r.enabled} style={{ cursor: 'default' }} className={r.enabled ? undefined : 'rule-paused'}>
                  <td>
                    <button className="tl-mini" aria-label="Move up" disabled={i === 0} onClick={() => void move(i, -1)}><ArrowUp size={12} /></button>
                    <button className="tl-mini" aria-label="Move down" disabled={i === rules.length - 1} onClick={() => void move(i, 1)}><ArrowDown size={12} /></button>
                  </td>
                  <td className="wrap" style={{ minWidth: 240 }}>
                    <strong>{r.name}</strong>{!r.enabled && <span className="pill warning" style={{ marginLeft: 6 }} data-testid="rule-paused">Paused</span>}
                    {r.trigger === 'watermark' && r.draining && r.enabled && <span className="pill accent" style={{ marginLeft: 6 }}>Draining</span>}
                    <div className="faint" style={{ fontSize: 12 }} data-testid="rule-description">{describeRule(r)}</div>
                    {!r.enabled && r.enabledChangedAt && (
                      <div className="faint" style={{ fontSize: 12 }} data-testid="rule-paused-by" title={dateTime(r.enabledChangedAt)}>
                        Paused{r.enabledChangedBy ? ` by ${r.enabledChangedBy.displayName}` : ''} {relative(r.enabledChangedAt)}
                      </div>
                    )}
                  </td>
                  <td>
                    <button type="button" role="switch" aria-checked={r.enabled} className="switch" disabled={busy === r.id} data-testid="rule-switch"
                      aria-label={`Run ${r.name} automatically`} onClick={() => void setEnabled(r, !r.enabled)}>
                      <span className="track" /><span>{r.enabled ? 'On' : 'Paused'}</span>
                    </button>
                  </td>
                  <td className="wrap" style={{ minWidth: 160 }}>
                    {r.lastResult ? <LastRun r={r} result={r.lastResult} /> : <span className="faint">Not yet</span>}
                  </td>
                  <td className="actions">
                    <button className="btn ghost sm" disabled={busy === r.id} onClick={() => void run(r, true)} title="Count what the rule would affect now"><Search size={13} /> Dry run</button>
                    <button className="btn ghost sm" disabled={busy === r.id} onClick={() => void run(r, false)} data-testid="run-rule"><Play size={13} /> Run now</button>
                    <button className="btn ghost sm" onClick={() => setEditing(r)} aria-label="Edit rule"><Pencil size={13} /></button>
                    <button className="btn ghost sm danger" onClick={() => void remove(r)} aria-label="Delete rule"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        )}
      </div>
      {editing && <RuleDialog rule={editing === 'new' ? null : editing} locations={locations} onClose={() => setEditing(null)} onSaved={refresh} />}
    </>
  );
}

function RuleDialog({ rule, locations, onClose, onSaved }: { rule: StorageRuleDTO | null; locations: StorageLocationSummary[]; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const categories = useCategories();
  const usable = locations.filter((l) => l.purpose === 'storage');
  const writable = usable.filter((l) => l.enabled && (l.kind !== 'agent' || l.agentFolder?.mode === 'managed' || l.agentRoot?.mode === 'managed'));
  const [f, setF] = useState({
    name: rule?.name ?? '',
    enabled: rule?.enabled ?? true,
    files: rule?.files ?? 'original',
    sourceLocationId: rule?.source.id ?? usable[0]?.id ?? '',
    action: rule?.action ?? 'copy',
    destinationLocationId: rule?.destination?.id ?? writable.find((l) => l.id !== (rule?.source.id ?? usable[0]?.id))?.id ?? '',
    trigger: rule?.trigger ?? 'age',
    afterDays: String(rule?.afterDays ?? 0),
    ageFrom: rule?.ageFrom ?? 'ingest',
    highWatermarkPct: String(rule?.highWatermarkPct ?? 90),
    lowWatermarkPct: String(rule?.lowWatermarkPct ?? 75),
    categoryId: rule?.category?.id ?? '',
    mediaKind: rule?.mediaKind ?? '',
    keepCopyLocationId: rule?.keepCopy?.id ?? '',
    windowId: rule?.window?.id ?? null as string | null,
    maxConcurrent: rule?.maxConcurrent == null ? '' : String(rule.maxConcurrent),
    primaryOnly: rule?.primaryOnly ?? false,
    versions: rule?.versions ?? 'all' as 'all' | 'noncurrent',
  });
  const coversMasters = f.files === 'original' || f.files === 'all';
  const windows = useTransferWindows();
  const maxConcurrent = f.maxConcurrent.trim() === '' ? null : Number(f.maxConcurrent);
  const concurrencyProblem = maxConcurrent != null && f.action !== 'delete' && !(Number.isInteger(maxConcurrent) && maxConcurrent >= 1 && maxConcurrent <= 64)
    ? 'At most 1 to 64 transfers at once, or leave it empty for no limit of its own.' : null;
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const watermark = f.trigger === 'watermark';
  const source = usable.find((l) => l.id === f.sourceLocationId);
  const high = Number(f.highWatermarkPct);
  const low = Number(f.lowWatermarkPct);
  const watermarkProblem = !watermark ? null
    : !(high >= 1 && high <= 100 && low >= 1 && low <= 100) ? 'Watermarks are between 1 and 100 %.'
      : low >= high ? 'The low watermark must be below the high watermark.' : null;
  const setTrigger = (t: 'age' | 'watermark') => setF((x) => ({ ...x, trigger: t, action: t === 'watermark' && x.action === 'copy' ? 'move' : x.action }));
  async function save() {
    setBusy(true);
    try {
      const body = {
        name: f.name, enabled: f.enabled, files: f.files, sourceLocationId: f.sourceLocationId, action: f.action,
        destinationLocationId: f.action === 'delete' ? null : f.destinationLocationId,
        trigger: f.trigger,
        afterDays: watermark ? null : Number(f.afterDays) || 0, ageFrom: f.ageFrom,
        highWatermarkPct: watermark ? high : null, lowWatermarkPct: watermark ? low : null,
        categoryId: f.categoryId || null, mediaKind: f.mediaKind || null, keepCopyLocationId: f.action === 'delete' ? f.keepCopyLocationId || null : null,
        windowId: f.windowId, maxConcurrent: f.action === 'delete' ? null : maxConcurrent,
        primaryOnly: coversMasters && f.primaryOnly,
        versions: f.versions,
      };
      const saved = await api<StorageRuleSaved>(rule ? `/api/v1/storage-rules/${rule.id}` : '/api/v1/storage-rules', { method: rule ? 'PUT' : 'POST', json: body });
      await onSaved();
      if (saved.warnings?.includes('NO_WATERMARK_BASE')) toast(`Saved ${f.name}, but it will never start: “${source?.name ?? 'The source'}” has no quota and PRISM doesn't know its disk size`, 'error');
      else toast(rule ? `Saved ${f.name}` : `Added ${f.name}`);
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={rule ? `Edit ${rule.name}` : 'New tiering rule'} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!f.name.trim() || !f.sourceLocationId || (f.action !== 'delete' && !f.destinationLocationId) || Boolean(watermarkProblem) || Boolean(concurrencyProblem) || busy} onClick={() => void save()} data-testid="save-rule">Save rule</button>
    </>}>
      <div className="field-row">
        <div className="field"><label htmlFor="rule-name">Name</label>
          <input id="rule-name" className="input" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder={watermark ? 'Free local media when it fills up' : 'Archive originals after 30 days'} /></div>
        <div className="field"><label htmlFor="rule-trigger">Starts when</label>
          <select id="rule-trigger" className="select" value={f.trigger} onChange={(e) => setTrigger(e.target.value as 'age' | 'watermark')}>
            <option value="age">Files reach an age</option>
            <option value="watermark">The source fills up (watermark)</option>
          </select></div>
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor="rule-action">Action</label>
          <select id="rule-action" className="select" value={f.action} onChange={(e) => set('action', e.target.value as typeof f.action)}>
            <option value="copy" disabled={watermark}>Copy (keep the file where it is)</option>
            <option value="move">Move (copy, verify, then delete the source)</option>
            <option value="delete">Delete (when a verified copy exists elsewhere)</option>
          </select>
          {watermark && <span className="help" data-testid="rule-copy-hint">A copy never makes the source less full, so a watermark rule moves or deletes.</span>}</div>
        <div className="field"><label htmlFor="rule-files">Files</label>
          <select id="rule-files" className="select" value={f.files} onChange={(e) => set('files', e.target.value as typeof f.files)}>
            {Object.entries(FILES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {coversMasters && (
            <label className="toggle-row" style={{ marginTop: 4 }}><input type="checkbox" checked={f.primaryOnly} onChange={(e) => set('primaryOnly', e.target.checked)} data-testid="rule-primary-only" />
              <span>Primary only<span className="help" style={{ display: 'block' }}>Originals include every master of an asset (a UHD and an HD master, Textless…). Tick to move or delete only the primary masters.</span></span></label>
          )}</div>
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor="rule-source">From</label>
          <select id="rule-source" className="select" value={f.sourceLocationId} onChange={(e) => set('sourceLocationId', e.target.value)}>
            {usable.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}</option>)}
          </select></div>
        {f.action === 'delete' ? (
          <div className="field"><label htmlFor="rule-keep">Only when a verified copy is in</label>
            <select id="rule-keep" className="select" value={f.keepCopyLocationId} onChange={(e) => set('keepCopyLocationId', e.target.value)}>
              <option value="">Any other location</option>
              {usable.filter((l) => l.id !== f.sourceLocationId).map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}</option>)}
            </select></div>
        ) : (
          <div className="field"><label htmlFor="rule-dest">To</label>
            <select id="rule-dest" className="select" value={f.destinationLocationId} onChange={(e) => set('destinationLocationId', e.target.value)}>
              {writable.filter((l) => l.id !== f.sourceLocationId).map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}{l.quota ? ` · quota: ${quotaUsedLabel(l.quota)}` : ''}</option>)}
            </select></div>
        )}
      </div>
      {watermark ? (
        <>
          <div className="field-row">
            <div className="field"><label htmlFor="rule-high">Start at (high watermark, %)</label>
              <input id="rule-high" className="input" type="number" min={1} max={100} step="any" value={f.highWatermarkPct} onChange={(e) => set('highWatermarkPct', e.target.value)} /></div>
            <div className="field"><label htmlFor="rule-low">Stop at (low watermark, %)</label>
              <input id="rule-low" className="input" type="number" min={1} max={100} step="any" value={f.lowWatermarkPct} onChange={(e) => set('lowWatermarkPct', e.target.value)} /></div>
          </div>
          {watermarkProblem && <p className="help" style={{ color: 'var(--danger)', marginTop: 0 }} data-testid="rule-watermark-problem">{watermarkProblem}</p>}
          {source && (fillText(source)
            ? <p className="help" style={{ marginTop: 0 }} data-testid="rule-fill">{fillText(source)}{source.fill.basis === 'disk' ? '. Other data on the disk counts too.' : '.'}
              {source.quota && diskBelowQuotaNote(source.quota, source.diskCapacity?.freeBytes) && <span style={{ color: 'var(--warning)', display: 'block' }} data-testid="rule-disk-note">{diskBelowQuotaNote(source.quota, source.diskCapacity?.freeBytes)}.</span>}</p>
            : (
              <div className="banner warning" role="note" data-testid="rule-no-base"><div className="banner-body">
                <strong>No size to measure</strong>
                <span>This location has no quota and PRISM doesn't know its disk size. Set a quota, or this rule will never start.</span>
              </div></div>
            ))}
          <p className="help" style={{ marginTop: 0 }}>Files used least recently go first. A delete never removes the last copy. Moves stop when the destination is full.</p>
        </>
      ) : (
        <div className="field-row">
          <div className="field"><label htmlFor="rule-days">After (days)</label>
            <input id="rule-days" className="input" type="number" min={0} step="any" value={f.afterDays} onChange={(e) => set('afterDays', e.target.value)} />
            <span className="help">0 runs on the next pass. Fractions work: 0.5 is twelve hours.</span></div>
          <div className="field"><label htmlFor="rule-age">Counted from</label>
            <select id="rule-age" className="select" value={f.ageFrom} onChange={(e) => set('ageFrom', e.target.value as typeof f.ageFrom)}>
              <option value="ingest">Ingest</option>
              <option value="arrival">When the file arrived in “From”</option>
              <option value="access">Last access</option>
            </select>
            {f.ageFrom === 'access' && <span className="help" data-testid="rule-access-help">Playback, downloads, exports and processing count; tiering moves don't. A file never used counts from when it arrived.</span>}</div>
        </div>
      )}
      <div className="field-row">
        <div className="field"><label htmlFor="rule-class">Only assets of class</label>
          <select id="rule-class" className="select" value={f.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
            <option value="">Any class</option>
            {categories.data?.items.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div className="field"><label htmlFor="rule-kind">Only media</label>
          <select id="rule-kind" className="select" value={f.mediaKind} onChange={(e) => set('mediaKind', e.target.value)}>
            <option value="">Video, audio and stills</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="image">Stills</option>
          </select></div>
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor="rule-versions">Which versions</label>
          <select id="rule-versions" className="select" value={f.versions} onChange={(e) => set('versions', e.target.value as 'all' | 'noncurrent')} data-testid="rule-versions">
            <option value="all">All versions</option>
            <option value="noncurrent">Only earlier versions (not the current one)</option>
          </select>
          {f.versions === 'noncurrent' && <span className="help" data-testid="rule-versions-help">For example archive old versions, or delete their copies here when another copy is kept. Versions in the recycle bin are never touched.</span>}</div>
        <div className="field" />
      </div>
      <div className="field"><label htmlFor="rule-window">When</label>
        <TransferWindowPicker id="rule-window" value={f.windowId} onChange={(v) => set('windowId', v)} allowCreate />
        <span className="help">Outside its window the rule waits{watermark ? ' (a draining rule carries on when the window opens)' : ''}. Run now ignores the window.</span></div>
      <div className="field-row">
        {f.action !== 'delete' && (
          <div className="field"><label htmlFor="rule-concurrent">At most N transfers at once</label>
            <input id="rule-concurrent" className="input" type="number" min={1} max={64} step={1} value={f.maxConcurrent} placeholder="No limit of its own"
              onChange={(e) => set('maxConcurrent', e.target.value)} data-testid="rule-concurrent" />
            {concurrencyProblem ? <span className="help" style={{ color: 'var(--danger)' }} data-testid="rule-concurrent-problem">{concurrencyProblem}</span>
              : <span className="help">The rule's own transfers, queued or running. PRISM's overall transfer limit still applies.</span>}</div>
        )}
        <div className="field" />
      </div>
      <ArchiveWarning f={f} locations={locations} />
      <p className="help" style={{ margin: 0 }} data-testid="rule-preview">{describeRule({
        files: f.files, mediaKind: (f.mediaKind || null) as StorageRuleDTO['mediaKind'], category: categories.data?.items.find((c) => c.id === f.categoryId) ?? null, trigger: f.trigger,
        afterDays: Number(f.afterDays) || 0, ageFrom: f.ageFrom, action: f.action, source: { id: f.sourceLocationId, name: source?.name ?? '' },
        destination: f.action === 'delete' ? null : { id: f.destinationLocationId, name: usable.find((l) => l.id === f.destinationLocationId)?.name ?? '' },
        keepCopy: f.keepCopyLocationId ? { id: f.keepCopyLocationId, name: usable.find((l) => l.id === f.keepCopyLocationId)?.name ?? '' } : null,
        highWatermarkPct: high, lowWatermarkPct: low,
        window: f.windowId ? { id: f.windowId, name: windows.data?.items.find((w) => w.id === f.windowId)?.name ?? 'its window' } : null,
        maxConcurrent: concurrencyProblem ? null : maxConcurrent,
        primaryOnly: coversMasters && f.primaryOnly,
        versions: f.versions,
      })}</p>
      <label className="toggle-row"><input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} data-testid="rule-enabled" /><span>Run this rule automatically</span></label>
    </Modal>
  );
}

/**
 * Archive tiers (0.29.0): only originals go into an archive, nothing is copied out of one without a restore,
 * and archives bill a minimum storage time, so deleting or moving files early is charged anyway.
 */
function ArchiveWarning({ f, locations }: { f: { action: string; files: string; sourceLocationId: string; destinationLocationId: string; keepCopyLocationId: string }; locations: StorageLocationSummary[] }) {
  const by = (id: string) => locations.find((l) => l.id === id);
  const src = by(f.sourceLocationId);
  const dst = f.action === 'delete' ? null : by(f.destinationLocationId);
  const notes: string[] = [];
  if (dst?.archive?.cold && f.files !== 'original') notes.push(`“${dst.name}” is an archive: a rule can only put originals there.`);
  if (src?.archive?.cold && f.action !== 'delete') notes.push(`“${src.name}” is an archive: its files must be restored before they can be copied. Use Restore on the assets instead.`);
  if (dst?.archive?.cold) notes.push(`Files in “${dst.name}” (${dst.archive.label}) must be restored before they can be read again, which takes ${dst.kind === 'oci' ? 'up to several hours' : 'hours'} and may be charged per GB.`);
  if (dst?.archive?.minimumDays) notes.push(`${dst.archive.label} bills at least ${dst.archive.minimumDays} days per file: deleting or moving files out of “${dst.name}” earlier is charged as if they stayed (early deletion fee).`);
  if (src?.archive?.minimumDays && f.action === 'delete') notes.push(`${src.archive.label} bills at least ${src.archive.minimumDays} days per file: deleting from “${src.name}” earlier is charged as if they stayed.`);
  if (!notes.length) return null;
  return (
    <div className="banner warning" role="note" data-testid="rule-archive-warning"><div className="banner-body">
      <strong>Archive tier</strong>
      {notes.map((n) => <span key={n}>{n}</span>)}
    </div></div>
  );
}
